import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { LoadingOverlay, Modal } from "@/components";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { Icon } from "@/components/Icon/Icon";
import {
	useAssignLibraryAssetMutation,
	useCreateMediaFolderMutation,
	useDeleteLibraryAssetMutation,
	useDeleteMediaFolderMutation,
	useGetMediaFoldersQuery,
	useGetMediaLibraryQuery,
	useMoveLibraryItemMutation,
	useUnassignLibraryAssetMutation,
	useUpdateMediaFolderMutation,
} from "@/pages/Private/redux/media/media.api";
import { LibraryAsset, MediaFolder } from "@/pages/Private/redux/media/media.schema";
import { classNames } from "@/utils/classNames";

import { MediaAssetCard } from "./MediaAssetCard";
import {
	FolderBreadcrumb,
	FolderSelection,
	LibraryDragItem,
	MediaFolderCard,
	MediaFolderTree,
	childrenOf,
	findFolder,
	folderIdOf,
} from "./MediaFolderTree";

interface MediaLibraryViewProps {
	/**
	 * The scenario being browsed from, where there is one. Its absence is what turns this
	 * from "this scenario's media, and what I can do with it here" into the standalone
	 * page: everything the company has, with nowhere to assign it to.
	 */
	courseId?: number;
}

/**
 * Everything this scenario has generated — and, on the other tab of the toggle, everything
 * the company has.
 *
 * The gap it closes was total. An asset could only become usable content at the moment it
 * was produced: the export named its own first format the trailer and there was no second
 * chance, and the other rendered shape, the scene videos and the scene images had no way to
 * become anything at all. Downloading a finished video and uploading it again was the only
 * way to change your mind.
 *
 * Reuse shares the stored object rather than copying it, so a file used by three scenarios
 * is one file. That is why deleting is offered only for a file nothing uses, and why the
 * server refuses anyway and names the scenarios if the UI is out of date.
 *
 * The folder tree beside the grid is the company's own shape for all of this. A file is in
 * at most one folder — it organises the file, not its uses — and the scope toggle, the kind
 * tabs and the sort all apply *within* the folder being looked at rather than instead of it.
 */
export const MediaLibraryView: FunctionComponent<MediaLibraryViewProps> = ({ courseId }) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) =>
			t(`courses.mediaLibrary.${key}`, options ?? {}),
		[t]
	);

	// Without a scenario there is nothing to narrow to, so the toggle disappears and the
	// company's whole library is what there is.
	const [scope, setScope] = useState<"scenario" | "company">(courseId ? "scenario" : "company");
	// One tab per kind of file; "all" is the whole library as before.
	const [kind, setKind] = useState<"all" | "video" | "image" | "audio" | "other">("all");
	// Newest first by default; the field pills choose date or name, the button flips it.
	const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");
	const [sortField, setSortField] = useState<"date" | "name">("date");
	// One scenario, or all of them — only offered when browsing the company's library.
	const [scenarioFilter, setScenarioFilter] = useState<number | null>(null);
	// Where in the tree the grid stands: the root, or one folder. Like a file manager, the
	// grid shows the folders inside it first and then its own files.
	const [folder, setFolder] = useState<FolderSelection>("root");
	const [includeSubfolders, setIncludeSubfolders] = useState(false);
	const [deletingFolder, setDeletingFolder] = useState<MediaFolder | null>(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [confirming, setConfirming] = useState<{ asset: LibraryAsset; assignment: string } | null>(
		null
	);
	// The lightbox: a video opened from its tile, played at a size worth watching.
	const [previewing, setPreviewing] = useState<LibraryAsset | null>(null);

	// The filter and order travel as query parameters — the server does the arranging,
	// this component renders what it gets. The scenario filter narrows the company view
	// by reusing the same courseId scoping the scenario view has always used.
	const { data: assets, isLoading } = useGetMediaLibraryQuery(
		{
			courseId: scope === "scenario" && courseId ? courseId : (scenarioFilter ?? undefined),
			kind: kind === "all" ? undefined : kind,
			sortBy: sortField,
			sortDir,
			folderId: folderIdOf(folder),
			// At the root too: there, "include subfolders" is every file in one flat grid.
			includeDescendants: includeSubfolders ? true : undefined,
		},
		// Refetched on every mount as well as on focus: exports and generations finish in
		// the background and nothing invalidates this cache — a library opened after an
		// export must show it.
		{ refetchOnFocus: true, refetchOnMountOrArgChange: true }
	);
	// The tree — and with it the scenarios the filter offers. One small request, where the
	// page used to fetch every file a second time just to learn which scenarios it had.
	const { data: tree, refetch: refetchTree } = useGetMediaFoldersQuery(undefined, {
		refetchOnFocus: true,
		refetchOnMountOrArgChange: true,
	});
	const folders = useMemo(() => tree?.folders ?? [], [tree]);
	const [assign, { isLoading: isAssigning }] = useAssignLibraryAssetMutation();
	const [unassign, { isLoading: isUnassigning }] = useUnassignLibraryAssetMutation();
	const [remove, { isLoading: isDeleting }] = useDeleteLibraryAssetMutation();
	const [createFolder] = useCreateMediaFolderMutation();
	const [updateFolder] = useUpdateMediaFolderMutation();
	const [deleteFolder, { isLoading: isDeletingFolder }] = useDeleteMediaFolderMutation();
	const [moveItem, { isLoading: isMoving }] = useMoveLibraryItemMutation();

	const isBusy = isAssigning || isUnassigning || isDeleting || isMoving;

	// A folder deleted from another tab is no place to stand; fall back to everything.
	useEffect(() => {
		if (tree && typeof folder === "number" && !findFolder(tree.folders, folder)) {
			setFolder("root");
		}
	}, [tree, folder]);

	// Already filtered and ordered by the server; rendered as received.
	const sorted = assets ?? [];
	// The folders inside the one being looked at, shown as tiles ahead of the files.
	const subfolders = childrenOf(folders, folder);

	// One dropdown for both sort decisions: what by, and which way.
	const sortOptions = useMemo(
		() => [
			{ id: "date:desc", title: ts("sortNewest") },
			{ id: "date:asc", title: ts("sortOldest") },
			{ id: "name:asc", title: ts("sortNameAsc") },
			{ id: "name:desc", title: ts("sortNameDesc") },
		],
		[ts]
	);

	// The scenarios present in the company's library, for the filter — the server names
	// them alongside the tree, so a chosen filter never hides the other scenarios from its
	// own menu and nothing has to be fetched twice to know them.
	const scenarioOptions = useMemo(
		() => (tree?.scenarios ?? []).map(({ id, name }) => ({ id, title: name || `#${id}` })),
		[tree]
	);

	const run = async (action: Promise<unknown>) => {
		setErrorMessage("");

		try {
			await action;
		} catch (error) {
			const body = (error as { data?: { message?: string } })?.data;

			setErrorMessage(body?.message || ts("actionFailed"));
		}
	};

	// ── folders ──────────────────────────────────────────────────────────────────

	const handleCreateFolder = (name: string, parentId: number | null) =>
		run(
			createFolder({ name, ...(parentId ? { parentId } : {}) })
				.unwrap()
				// Straight into the new folder: what was just made is where the next drop goes.
				// Only once the tree holds it: selected earlier, the fallback above finds it
				// missing from the stale tree and puts the grid back at the root (BCM360-335).
				.then(async (created) => {
					await refetchTree();
					setFolder(created.id);
				})
		);

	const handleRenameFolder = (id: number, name: string) => run(updateFolder({ id, name }).unwrap());

	const handleMoveFolder = (id: number, parentId: number | null) =>
		run(updateFolder({ id, parentId }).unwrap());

	const handleDropItem = (folderId: number | null, item: LibraryDragItem) =>
		run(moveItem({ folderId, ...item }).unwrap());

	const handleDeleteFolder = async () => {
		if (!deletingFolder) {
			return;
		}

		const { id } = deletingFolder;

		await run(
			deleteFolder(id)
				.unwrap()
				.then(() => {
					if (folder === id) {
						setFolder("root");
					}
				})
		);
		setDeletingFolder(null);
	};

	const handleAssign = (asset: LibraryAsset, fileAssignment: string) => {
		// The scenario browsed from — or, on the standalone page, the scenario that
		// generated the file, which is where "use as intro" for an export belongs.
		const target = courseId ?? asset.sourceCourseId;

		if (!target) {
			return;
		}

		// Replacing the intro video changes what trainees see, so it asks first. Adding
		// content does not — nothing is displaced by it.
		const replacesTrailer =
			fileAssignment === "TrailerVideo" &&
			(assets ?? []).some((candidate) =>
				candidate.usages.some(
					(usage) => usage.courseId === target && usage.fileAssignment === "TrailerVideo"
				)
			);

		if (replacesTrailer) {
			setConfirming({ asset, assignment: fileAssignment });

			return;
		}

		void run(
			assign({ kind: asset.kind, assetId: asset.id, courseId: target, fileAssignment }).unwrap()
		);
	};

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="max-w-[640px] text-sm text-subtle">
					{ts(courseId ? "description" : "descriptionGlobal")}
				</p>

				{!!courseId && (
					<div className="flex gap-1 rounded-full bg-surface p-1">
						{(["scenario", "company"] as const).map((option) => (
							<button
								key={option}
								aria-pressed={scope === option}
								// The selected pill matches step 5's format chips: the tint carries the
								// selection and the label stays dark. `accent` is a fill, never an ink.
								className={classNames(
									"rounded-full border px-3 py-1.5 text-xs font-semibold",
									scope === option
										? "border-accent bg-accent-tint text-ink"
										: "border-transparent bg-white text-subtle"
								)}
								type="button"
								onClick={() => setScope(option)}
							>
								{ts(option === "scenario" ? "scopeScenario" : "scopeCompany")}
							</button>
						))}
					</div>
				)}
			</div>

			<div className="flex flex-col gap-4 md:flex-row">
				<aside className="shrink-0 md:w-56">
					<MediaFolderTree
						folders={folders}
						selected={folder}
						onCreate={handleCreateFolder}
						onDelete={setDeletingFolder}
						onDropItem={handleDropItem}
						onMoveFolder={handleMoveFolder}
						onRename={handleRenameFolder}
						onSelect={setFolder}
					/>
				</aside>

				<div className="min-w-0 flex-1 space-y-4">
					<FolderBreadcrumb
						folders={folders}
						includeSubfolders={includeSubfolders}
						selected={folder}
						onIncludeSubfolders={setIncludeSubfolders}
						onSelect={setFolder}
					/>

					<div className="flex flex-wrap items-center justify-between gap-2">
						{/* The kind tabs on the left — the grid below shows only that kind of file. */}
						<div className="flex w-fit flex-wrap gap-1 rounded-full bg-surface p-1">
							{(["all", "video", "image", "audio", "other"] as const).map((option) => (
								<button
									key={option}
									aria-pressed={kind === option}
									className={classNames(
										"rounded-full border px-3 py-1.5 text-xs font-semibold",
										kind === option
											? "border-accent bg-accent-tint text-ink"
											: "border-transparent bg-white text-subtle"
									)}
									type="button"
									onClick={() => setKind(option)}
								>
									{ts(`kindTab.${option}`)}
								</button>
							))}
						</div>

						{/* Sort and filter on the right, both as dropdowns at the tab row's height. */}
						<div className="flex flex-wrap items-center gap-2">
							<div className="min-w-[180px]">
								<Dropdown
									floating
									classNameButton="!h-[38px] !py-0 items-center text-xs"
									data={sortOptions}
									handleSelect={(item) => {
										const [field, dir] = String(item.id).split(":");

										setSortField(field as "date" | "name");
										setSortDir(dir as "asc" | "desc");
									}}
									value={sortOptions.find((option) => option.id === `${sortField}:${sortDir}`)}
								/>
							</div>

							{/* The scenario filter — only where the list can span several scenarios. */}
							{(scope === "company" || !courseId) && scenarioOptions.length > 1 && (
								<div className="min-w-[220px]">
									<Dropdown
										floating
										classNameButton="!h-[38px] !py-0 items-center text-xs"
										data={[{ id: 0, title: ts("filterAllScenarios") }, ...scenarioOptions]}
										handleSelect={(item) =>
											setScenarioFilter(Number(item.id) === 0 ? null : Number(item.id))
										}
										value={
											scenarioFilter === null
												? { id: 0, title: ts("filterAllScenarios") }
												: (scenarioOptions.find((option) => option.id === scenarioFilter) ?? {
														id: 0,
														title: ts("filterAllScenarios"),
													})
										}
									/>
								</div>
							)}
						</div>
					</div>

					{!!errorMessage && (
						<p className="flex gap-2 rounded-card bg-error-light px-3 py-2 text-xs text-text">
							<Icon icon={faTriangleExclamation} />
							<span>{errorMessage}</span>
						</p>
					)}

					{isLoading ? <LoadingOverlay /> : null}

					{!isLoading && !sorted.length && !subfolders.length && (
						<p className="rounded-card border border-dashed border-border bg-surface px-4 py-10 text-center text-sm text-subtle">
							{ts(typeof folder === "number" ? "folders.emptyFolder" : "empty")}
						</p>
					)}

					{subfolders.length > 0 && (
						<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
							{subfolders.map((child) => (
								<MediaFolderCard
									key={child.id}
									draggable
									folder={child}
									onDropItem={handleDropItem}
									onMoveFolder={handleMoveFolder}
									onOpen={setFolder}
								/>
							))}
						</div>
					)}

					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{sorted.map((asset) => (
							<MediaAssetCard
								key={`${asset.kind}-${asset.id}`}
								draggable
								asset={asset}
								courseId={courseId}
								isBusy={isBusy}
								onAssign={handleAssign}
								onDelete={(target) => void run(remove(target.id).unwrap())}
								onPreview={setPreviewing}
								onUnassign={(courseFileId) => void run(unassign(courseFileId).unwrap())}
							/>
						))}
					</div>
				</div>
			</div>

			{/* The player modal — the app's own dialog, not a bare overlay. The tile showed
			    the poster; this is where the video actually plays. */}
			{!!previewing && (
				<Modal
					handleClose={() => setPreviewing(null)}
					isOpened={!!previewing}
					showActions={false}
					// The dialog takes the video's orientation: a 9:16 export gets a narrow
					// box instead of floating in a wide one.
					size={
						previewing.width && previewing.height && previewing.height > previewing.width
							? "sm"
							: "xl"
					}
					title={previewing.fileName ?? ts("kindVideo")}
				>
					<video
						autoPlay
						controls
						// Sized by the video's own aspect ratio — the stored dimensions shape
						// the box before the metadata has even loaded.
						className="mx-auto max-h-[70vh] w-auto max-w-full rounded-card bg-ink"
						src={previewing.fullFilePath}
						style={
							previewing.width && previewing.height
								? { aspectRatio: `${previewing.width} / ${previewing.height}` }
								: undefined
						}
					/>
				</Modal>
			)}

			{/* Deleting a folder asks first, like replacing a trailer does. The server refuses a
			    folder that still holds anything and says what is in it; a file is never deleted
			    along with a folder. */}
			<Modal
				handleClose={() => setDeletingFolder(null)}
				handleSave={handleDeleteFolder}
				isLoading={isDeletingFolder}
				isOpened={Boolean(deletingFolder)}
				size="sm"
				submitButtonText={ts("folders.deleteConfirm")}
				title={ts("folders.deleteTitle", { name: deletingFolder?.name ?? "" })}
			>
				<p className="text-sm text-text">{ts("folders.deleteBody")}</p>
			</Modal>

			<Modal
				handleClose={() => setConfirming(null)}
				handleSave={async () => {
					const target = courseId ?? confirming?.asset.sourceCourseId;

					if (confirming && target) {
						await run(
							assign({
								kind: confirming.asset.kind,
								assetId: confirming.asset.id,
								courseId: target,
								fileAssignment: confirming.assignment,
							}).unwrap()
						);
					}

					setConfirming(null);
				}}
				isLoading={isAssigning}
				isOpened={Boolean(confirming)}
				size="sm"
				submitButtonText={ts("replaceConfirm")}
				title={ts("replaceTitle")}
			>
				<p className="text-sm text-text">{ts("replaceBody")}</p>
			</Modal>
		</div>
	);
};
